/* eslint-disable no-bitwise */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export interface NormalizedRGB {
  red: number;
  green: number;
  blue: number;
  alpha: number;
}

export function hexToRgba(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : {
      r: 0,
      g: 0,
      b: 0,
    };
}

export function normalizeRGB(rgbObj: RGB): NormalizedRGB {
  return {
    red: rgbObj.r / 255,
    green: rgbObj.g / 255,
    blue: rgbObj.b / 255,
    alpha: 1,
  };
}

const clamp = (val: number) => Math.min(1, Math.max(0, val));

export function componentToHex(c: number): string {
  const hex = Math.round(clamp(c) * 255).toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

export function rgbToHex({ red, green, blue }: NormalizedRGB): string {
  return `#${componentToHex(red)}${componentToHex(green)}${componentToHex(
    blue
  )}`;
}

export function getContrastText(
  { r, g, b }: RGB,
  threshold: number
): "black" | "white" {
  const contrast =
    (Math.round(r * 299) + Math.round(g * 587) + Math.round(b * 114)) / 1000;
  return contrast >= threshold ? "black" : "white";
}

export function rotateColorBy(hex: string, rotationAmount: number): string {
  const rgb = hexToRgba(hex) as RGB;
  let r = rgb.r / 255.0;
  let g = rgb.g / 255.0;
  let b = rgb.b / 255.0;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h: number;
  let s: number;
  const l = (max + min) / 2.0;

  if (max === min) {
    h = 0;
    s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2.0 - max - min) : d / (max + min);
    if (max === r && g >= b) h = (1.0472 * (g - b)) / d;
    else if (max === r && g < b) h = (1.0472 * (g - b)) / d + 6.2832;
    else if (max === g) h = (1.0472 * (b - r)) / d + 2.0944;
    else h = (1.0472 * (r - g)) / d + 4.1888; // max === b
  }

  h = (h / 6.2832) * 360.0 + 0;
  h += rotationAmount;
  if (h > 360) h -= 360;
  h /= 360;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      let temp = t;
      if (temp < 0) temp += 1;
      if (temp > 1) temp -= 1;
      if (temp < 1 / 6) return p + (q - p) * 6 * temp;
      if (temp < 1 / 2) return q;
      if (temp < 2 / 3) return p + (q - p) * (2 / 3 - temp) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  r = Math.round(r * 255);
  g = Math.round(g * 255);
  b = Math.round(b * 255);

  const final = b | (g << 8) | (r << 16);
  return `#${(0x1000000 | final).toString(16).substring(1)}`;
}

export function arrayToPalette(
  colors: string[],
  keys: number[] = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900],
  accentKeys: string[] = ["A1A100", "A1A200", "A1A400", "A1A700"]
): Record<string | number, string> {
  const palette: Record<string | number, string> = {};

  for (let i = 0; i < keys.length && i < colors.length; i++) {
    palette[keys[i]] = colors[i];
  }

  for (let i = 0; i < accentKeys.length && i + keys.length < colors.length; i++) {
    palette[accentKeys[i]] = colors[i + keys.length];
  }

  return palette;
}
